import { observer } from "mobx-react";
import { v4 as uuid } from "uuid";
import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useTheme,
} from "@mui/material";

import { useAppStore } from "../../../hooks/useAppStore";
import { useTicketStore } from "../../../hooks/useTicketStore";
import DropZoneWrapper from "../DropZoneWrapper";
import useNotifier from "../../../hooks/useNotifier";
import {
  CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH,
  CONTACT_SUPPORT_PURPOSE_TYPES,
  ContactSupportFormType,
} from "../../../config/constants";
import { CloseIcon } from "../../icons";
import { ContactSupportUploadFile } from "../../../stores/AppStore";
import ContactSupportModalFilesDisplay from "./contact-support-components/old/ContactSupportModalFilesDisplay";
import ContactSupportModalFormContent from "./contact-support-components/old/ContactSupportModalFormContent";

interface ContactSupportDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const initialFormData: ContactSupportFormType = {
  patientName: "",
  mrn: "",
};

/**
 * Validate customer support form data
 * Checks for empthy values and length limits of the fields
 * @returns true if form is invalid, false otherwise
 */
const isFormInvalid = (
  formData: ContactSupportFormType,
  purpose: string,
  uploadedFiles: ContactSupportUploadFile[],
): boolean => {
  if (!purpose) {
    return true;
  }

  // Support purpose validation
  if (purpose === CONTACT_SUPPORT_PURPOSE_TYPES.SUPPORT) {
    return (
      !formData.subject?.trim() ||
      !formData.comment?.trim() ||
      formData?.subject?.length >
        CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.subject ||
      formData?.comment?.length > CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.comment
    );
  }

  // Mandatory fields for other purposes
  if (
    !formData?.patientName?.trim() ||
    !formData?.mrn?.trim() ||
    uploadedFiles?.filter((file) => file.status !== "uploaded")?.length === 0 ||
    formData?.patientName?.length >
      CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.patientName ||
    formData?.mrn?.length > CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.mrn
  ) {
    return true;
  }

  switch (purpose) {
    case CONTACT_SUPPORT_PURPOSE_TYPES.PRESERVICE_REVIEW:
      return !formData?.surgeryDate;
    case CONTACT_SUPPORT_PURPOSE_TYPES.DENIAL_MANAGEMENT: {
      return (
        !formData?.claimNumber?.trim() ||
        formData?.claimNumber?.length >
          CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.claimNumber ||
        !formData?.denialType?.trim()
      );
    }
  }

  return false;
};

const ContactSupportMenuDialog: FC<ContactSupportDialogProps> = observer(
  ({ isOpen, onClose }) => {
    const theme = useTheme();
    const notify = useNotifier();

    const [purpose, setPurpose] = useState<string>("");
    const [formData, setFormData] =
      useState<ContactSupportFormType>(initialFormData);

    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [responseStatus, setResponseStatus] = useState<{
      status: "success" | "error" | "warning";
      message?: string;
    } | null>(null);

    const {
      contactSupportUploadFiles: uploadedFiles,
      setContactSupportUploadFiles: setUploadedFiles,
      triggerUploadContactSupportFiles,
      isUploadSuccessful,
      setIsUploadSuccessful,
    } = useAppStore();

    const isUploadDisabled = useMemo(
      () => isFormInvalid(formData, purpose, uploadedFiles),
      [formData, purpose, uploadedFiles],
    );

    useEffect(() => {
      if (isOpen) {
        resetStates();
      }
    }, [isOpen]);

    const resetStates = () => {
      setPurpose("");
      setFormData(initialFormData);
      setUploadedFiles([]);
      setIsUploading(false);
      setResponseStatus(null);
      setIsUploadSuccessful(false);
    };

    const onDropFiles = (selectedFiles: File[]) => {
      if (!selectedFiles || selectedFiles.length === 0) {
        notify.showWarning("Please select at least one file");
        return;
      }

      setUploadedFiles([
        ...uploadedFiles,
        ...Array.from(selectedFiles).map((file) => ({
          file,
          status: "attached" as const,
          id: uuid(),
        })),
      ]);
    };

    const { seedTicket } = useTicketStore();

    const handleClose = () => {
      onClose();
      resetStates();
    };

    const handleOpenNewTicket = () => {
      resetStates();
    };

    const handleConfirm = async () => {
      setIsUploading(true);
      setResponseStatus(null);

      const response = await triggerUploadContactSupportFiles({
        ...formData,
        purpose,
      } as any);

      if (response.ticket) seedTicket(response.ticket);
      setResponseStatus(response);
      setIsUploading(false);
    };

    return (
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth={
          isUploadSuccessful ? "xs" : uploadedFiles.length > 0 ? "lg" : "xs"
        }
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: "19px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Contact Support
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "& svg": { width: 20, height: 20 },
              }}
            >
              <CloseIcon className="" />
            </Box>
          </IconButton>
        </DialogTitle>

        {isUploadSuccessful ? (
          <DialogContent sx={{ px: 3, py: 2 }}>
            <Alert severity="success" sx={{ mb: 2, mt: 4 }}>
              Your ticket was successfully submitted to the support team.
            </Alert>
          </DialogContent>
        ) : (
          <DialogContent sx={{ px: 3, py: 2 }}>
            <DropZoneWrapper
              className="flex flex-col h-full overflow-hidden"
              overlayClassName="ml-0 mr-0"
              onFilesDropped={(files) => onDropFiles(files as any)}
              disabled={isUploading || !purpose}
            >
              <Grid container spacing={2} mb={2} sx={{ paddingTop: "20px" }}>
                {/* Form */}
                <Grid
                  size={uploadedFiles.length > 0 ? 6 : 12}
                  sx={{ display: "grid", gridGap: "7px" }}
                >
                  <ContactSupportModalFormContent
                    purpose={purpose}
                    setPurpose={setPurpose}
                    formData={formData}
                    setFormData={setFormData}
                    isDisabled={isUploading}
                  />
                </Grid>

                {/* Uploaded files display */}
                {uploadedFiles.length > 0 ? (
                  <Grid size={6}>
                    <ContactSupportModalFilesDisplay disabled={isUploading} />
                  </Grid>
                ) : null}

                {responseStatus && responseStatus.message ? (
                  <Grid size={12}>
                    <Alert severity={responseStatus.status} sx={{ mt: 1 }}>
                      {responseStatus.message}
                    </Alert>
                  </Grid>
                ) : null}
              </Grid>
            </DropZoneWrapper>
          </DialogContent>
        )}

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {isUploadSuccessful ? (
            <>
              <Button onClick={handleClose} variant="outlined">
                Close
              </Button>
              <Button onClick={handleOpenNewTicket} variant="contained">
                Open New Ticket
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleClose}
                disabled={isUploading}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={isUploadDisabled || isUploading}
                variant="contained"
                loading={isUploading}
              >
                Upload
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  },
);

export default ContactSupportMenuDialog;
